import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';

import { Discount, StripeSource } from '../models';
import { ValueService } from './value.service';
import { AppSettingsService } from './app-settings.service';

declare var Stripe: any;

@Injectable({ providedIn: 'root' })
export class StripeService {
  private readonly _componentName = 'StripeService';
  
  /**
   * The Stripe Test Base URL
   */
  public stripeTestBaseUrl = 'https://dashboard.stripe.com/test';

  /**
   * The Stripe Production Base URL
   */
  public stripeProductionBaseUrl = 'https://dashboard.stripe.com';

  constructor(
    readonly appSettingsService: AppSettingsService,
    readonly zone: NgZone,
    private valueHelper: ValueService,
    private http: HttpClient
  ) {}

  private setPublicKey() {
    if (this.appSettingsService.stripePublicKey === undefined) {
      throw Error('Stripe public key not found');
    }
    Stripe.setPublishableKey(this.appSettingsService.stripePublicKey);
  }

  /**
   * Is stripe test environment?
   */
  public get isTest(): boolean {
    if (this.appSettingsService.stripePublicKey) {
      return this.appSettingsService.stripePublicKey.includes('test');
    } else {
      return true;
    }
  }

  /**
   * Gets the URL link to a stripe charge
   */
  public getStripeChargeUrl(chargeId: string) {
    if (this.isTest) {
      return `https://dashboard.stripe.com/test/payments/${chargeId}`;
    } else {
      return `https://dashboard.stripe.com/payments/${chargeId}`;
    }
  }

  //

  /* When using Stripe.js, you should ensure the following:
The card number, expiration, CVC and amount are set correctly in createToken.
In the response handler for createToken, card errors are handled and displayed properly.
Only a valid token is passed to your server as part of payment form submission.
Sensitive card details (number, expiration and CVC) are not included in form data submitted to your server.
  */

  // Submit card data to Stripe, return a db-compatible StripeSource.
  getToken(card: any) {
    this.setPublicKey();
    return new Observable<StripeSource>(subscriber => {
      Stripe.card.createToken(card, (status, response) => {
        // Wrapping inside the NgZone
        this.zone.run(() => {
          if (status === 200) {
            subscriber.next(this.normalizeTokenResponse(response));
            subscriber.complete();
          } else {
            subscriber.error(response.error);
          }
        });
      });
    });
  }

  getCoupon(couponCode: string) {
    return this.http.get<Discount>(`stripe/coupon/${couponCode}`);
  }

  getAccountSources(userId?: number) {
    if (userId === undefined) {
      return this.http.get<StripeSource[]>('stripe/sources');
    } else {
      // This will only work for Admins
      return this.http.get<StripeSource[]>(`stripe/sources/${userId}`);
    }
  }

  /**
   * Convert stripe token model to db StripeSource model.
   *  brand is missing from the declaration of StripeCardData but is present in the response.
   */
  private normalizeTokenResponse(token: any): StripeSource {
    return {
      stripeSourceId: token.id,
      last4: token.card.last4,
      brand: token.card.brand,
      expMonth: token.card.exp_month,
      expYear: token.card.exp_year,
      name: token.card.name
    } as StripeSource;
  }

  cardType(cardNumber: string) {
    this.setPublicKey();
    return Stripe.cardType(cardNumber);
  }

  brandIconCSSClass(brand: any) {
    return this.cardIcons[brand];
  }

  readonly cardIcons = Object.freeze({
    Visa: 'fa-brands fa-cc-visa',
    MasterCard: 'fa-brands fa-cc-mastercard',
    Discover: 'fa-brands fa-cc-discover',
    'American Express': 'fa-brands fa-cc-amex',
    JCB: 'fa-brands fa-cc-jcb',
    'Diners Club': 'fa-brands fa-cc-diners-club',
    Unknown: 'fa-regular fa-credit-card'
  });
}
